import React from 'react';
import { Loader2, Paperclip, Send, Sparkles, X, Zap } from 'lucide-react';
import type { Macro } from '../../services/supportApi';
import { AI_ACTIONS, FILE_ACCEPT, formatFileSize } from './constants';

interface ThreadComposerProps {
  /** `operator`: risposta pubblica nel dettaglio operatore. `requester`: box risposta del richiedente. */
  variant: 'operator' | 'requester';
  value: string;
  onChange: (value: string) => void;
  files: File[];
  /** Riceve i file scelti: la validazione (tipo/dimensione/numero) sta nel chiamante. */
  onAddFiles: (picked: File[]) => void;
  onRemoveFile: (index: number) => void;
  onSend: () => void;
  busy?: boolean;

  // ===== Solo variante operatore =====
  macros?: Macro[];
  onInsertMacro?: (macro: Macro) => void;
  aiUnavailable?: boolean;
  /** Spinner sul bottone AI di QUESTO composer. */
  aiLoading?: boolean;
  /** Una richiesta AI e in corso (anche sull'altro composer): bottone disabilitato. */
  aiBusy?: boolean;
  aiProposal?: string | null;
  onRunAi?: (action: string) => void;
  onAcceptAi?: () => void;
  onDismissAi?: () => void;
  /** Menu aperto per QUESTO composer: la mutua esclusione è gestita dal chiamante. */
  openMenu?: 'macro' | 'ai' | null;
  onToggleMenu?: (kind: 'macro' | 'ai') => void;
}

// Composer del thread pubblico. Le due varianti condividono la logica (testo,
// allegati, invio) ma non il markup: quello resta identico alle pagine originali.
const ThreadComposer: React.FC<ThreadComposerProps> = ({
  variant,
  value,
  onChange,
  files,
  onAddFiles,
  onRemoveFile,
  onSend,
  busy = false,
  macros = [],
  onInsertMacro,
  aiUnavailable = false,
  aiLoading = false,
  aiBusy = false,
  aiProposal = null,
  onRunAi,
  onAcceptAi,
  onDismissAi,
  openMenu = null,
  onToggleMenu,
}) => {
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // consente di riselezionare lo stesso file
    if (picked.length > 0) onAddFiles(picked);
  };

  const nothingToSend = !value.trim() && files.length === 0;

  if (variant === 'requester') {
    return (
      <div className="space-y-2.5">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Scrivi un messaggio..."
          rows={3}
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
        />

        {/* Allegati */}
        <div>
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors">
            <Paperclip className="w-3.5 h-3.5 shrink-0" />
            <span>Allega file</span>
            <input type="file" accept={FILE_ACCEPT} multiple onChange={pick} className="hidden" />
          </label>
          {files.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/60 text-xs"
                >
                  <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-gray-700 dark:text-gray-200">{f.name}</span>
                  <span className="text-gray-400 dark:text-gray-500 shrink-0">{formatFileSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => onRemoveFile(i)}
                    className="p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors shrink-0"
                    aria-label={`Rimuovi ${f.name}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <button
          onClick={onSend}
          disabled={busy || nothingToSend}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Invio...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Invia messaggio
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder="Scrivi una risposta all'associato…"
        className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 px-3 py-2 resize-none"
      />
      {aiProposal !== null && (
        <div className="mt-2 rounded-md border border-purple-200 dark:border-purple-800/50 bg-purple-50 dark:bg-purple-900/20 p-2">
          <div className="text-2xs font-medium text-purple-700 dark:text-purple-300 mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Proposta AI
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
            {aiProposal}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={onDismissAi}
              className="text-2xs px-2.5 py-1 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={onAcceptAi}
              className="text-2xs px-2.5 py-1 rounded-md bg-purple-600 text-white hover:bg-purple-700"
            >
              Usa
            </button>
          </div>
        </div>
      )}
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li
              key={i}
              className="flex items-center gap-2 text-2xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded px-2 py-1"
            >
              <Paperclip className="w-3 h-3 flex-shrink-0" />
              <span className="truncate flex-1">{f.name}</span>
              <button
                type="button"
                onClick={() => onRemoveFile(i)}
                className="text-gray-400 hover:text-red-500"
                aria-label="Rimuovi allegato"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center justify-between mt-2 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
            <Paperclip className="w-4 h-4" /> Allega
            <input type="file" accept={FILE_ACCEPT} multiple className="hidden" onChange={pick} />
          </label>

          {/* Risposte rapide (macro) */}
          {macros.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => onToggleMenu?.('macro')}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Zap className="w-4 h-4" /> Risposte rapide
              </button>
              {openMenu === 'macro' && (
                <div className="absolute z-20 bottom-full mb-1 left-0 w-64 max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-1">
                  {macros.map((m) => (
                    <button
                      key={m._id}
                      type="button"
                      onClick={() => onInsertMacro?.(m)}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 truncate"
                    >
                      {m.titolo}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Assist AI */}
          {!aiUnavailable && (
            <div className="relative">
              <button
                type="button"
                disabled={aiBusy}
                onClick={() => onToggleMenu?.('ai')}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-white dark:bg-gray-800 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50 hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50"
              >
                {aiLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}{' '}
                AI
              </button>
              {openMenu === 'ai' && (
                <div className="absolute z-20 bottom-full mb-1 left-0 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-1">
                  {AI_ACTIONS.map((a) => (
                    <button
                      key={a.action}
                      type="button"
                      onClick={() => onRunAi?.(a.action)}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onSend}
          disabled={busy || nothingToSend}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex-shrink-0"
        >
          <Send className="w-4 h-4" /> Invia risposta
        </button>
      </div>
    </>
  );
};

export default ThreadComposer;
